import React, { useState, useEffect, useContext, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { BrowserProvider, JsonRpcProvider, Contract, ethers } from 'ethers';
import { AuthContext } from '../AuthContext.js';

const WEB3FX_CONTRACT_ADDRESS = '0x6FAFdfF2B006F314635EA50A8AC6d7dA1D07427c';
const WEB3FX_ABI = [
  "function executeSwap(address tokenIn, address tokenOut, uint256 amountIn, uint256 amountOut, uint256 feeAmount, bytes32 txHash) external",
  "function addToken(address token) external",
  "function removeToken(address token) external",
  "function getUserSwaps(address) external view returns (tuple(address user, address tokenIn, address tokenOut, uint256 amountIn, uint256 amountOut, uint256 feeAmount, uint256 timestamp, bytes32 txHash)[])",
  "function getTotalSwaps() external view returns (uint256)",
  "function getTotalFeesCollected() external view returns (uint256)",
  "function withdrawFees() external",
  "function supportedTokens(address) external view returns (bool)",
  "function owner() external view returns (address)",
  "function REVENUE_WALLET() external view returns (address)",
  "function FEE_PERCENT() external view returns (uint256)",
  "function MIN_SWAP_AMOUNT() external view returns (uint256)"
];

// Supported tokens with their contract addresses
const SUPPORTED_TOKENS = [
  { symbol: 'ETH', address: '0xFFf9976782D46cC05630d1f6eBaB18B2c6D2D1d9' }, // WETH
  { symbol: 'USDC', address: '0xA0b86a33E6417c47F9b02c3AD64fc8d5A91b077b' }
];

const Web3FX = () => {
  const { isAuthenticated, ensName, loginWithSIWE, isENSHolder } = useContext(AuthContext) || {};
  const [account, setAccount] = useState(null);
  const [fromToken, setFromToken] = useState('ETH');
  const [toToken, setToToken] = useState('USDC');
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [slippage, setSlippage] = useState('1.0');
  const [selectedTab, setSelectedTab] = useState('swap');
  const [error, setError] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [swapLoading, setSwapLoading] = useState(false);
  const [contractData, setContractData] = useState({
    totalSwaps: 0,
    totalFees: '0',
    minSwapAmount: '0',
    feePercent: '0.3',
    revenueWallet: '0x74338Aea95f8e80D64944f3F2adAC4c2e7Da919a'
  });
  const [swapHistory, setSwapHistory] = useState([]);
  const [tokenPrices, setTokenPrices] = useState([]);
  const contractRef = useRef(null);

  // Get provider - only blockchain connections
  const getProvider = async () => {
    try {
      const provider = new BrowserProvider(window.ethereum);
      const network = await provider.getNetwork();
      if (network.chainId !== 11155111n) {
        await provider.send("wallet_switchEthereumChain", [{ chainId: "0xAA36A7" }]);
        await new Promise(resolve => setTimeout(resolve, 4000));
      }
      return provider;
    } catch (err) {
      console.warn("MetaMask failed, falling back to Infura:", err.message);
      return new JsonRpcProvider("https://sepolia.infura.io/v3/674854f82eaa44c994c2eb347b01b279");
    }
  };

  // Initialize contract and fetch data
  useEffect(() => {
    if (window.ethereum && isAuthenticated && isENSHolder) {
      initializeContract();
    }
  }, [isAuthenticated, isENSHolder]);

  const initializeContract = async () => {
    try {
      const provider = await getProvider();
      const accounts = await provider.send("eth_requestAccounts", []);
      setAccount(accounts[0]);
      const signer = await provider.getSigner();
      const contract = new Contract(WEB3FX_CONTRACT_ADDRESS, WEB3FX_ABI, signer);
      contractRef.current = contract;

      await fetchContractData(contract);
      await fetchSwapHistory(contract, accounts[0]);
      await fetchTokenPrices(); // Simulate prices since we don't have API
    } catch (err) {
      setError("Failed to connect: " + err.message);
    }
  };

  const fetchContractData = async (contract) => {
    try {
      const [totalSwaps, totalFees, minSwapAmount, feePercent, revenueWallet] = await Promise.all([
        contract.getTotalSwaps(),
        contract.getTotalFeesCollected(),
        contract.MIN_SWAP_AMOUNT(),
        contract.FEE_PERCENT(),
        contract.REVENUE_WALLET()
      ]);

      setContractData({
        totalSwaps: Number(totalSwaps),
        totalFees: ethers.formatEther(totalFees),
        minSwapAmount: ethers.formatEther(minSwapAmount),
        feePercent: (Number(feePercent) / 100).toFixed(1),
        revenueWallet
      });
    } catch (err) {
      console.warn("Contract data fetch failed:", err);
    }
  };

  const fetchSwapHistory = async (contract, userAddress) => {
    try {
      const userSwaps = await contract.getUserSwaps(userAddress);
      const formattedSwaps = userSwaps.map((swap, index) => ({
        id: index,
        fromToken: getTokenSymbol(swap.tokenIn),
        toToken: getTokenSymbol(swap.tokenOut),
        amountIn: ethers.formatEther(swap.amountIn),
        amountOut: ethers.formatEther(swap.amountOut),
        feeAmount: ethers.formatEther(swap.feeAmount),
        txHash: swap.txHash,
        status: 'confirmed',
        createdAt: new Date(Number(swap.timestamp) * 1000).toISOString()
      }));

      setSwapHistory(formattedSwaps);
    } catch (err) {
      console.warn("Failed to fetch swap history:", err);
      setSwapHistory([]);
    }
  };

  // Simulate token prices using blockchain data (no API calls)
  const fetchTokenPrices = async () => {
    // Mock prices since we don't have price oracle in contract
    const mockPrices = [
      { symbol: 'ETH', priceUSD: 2500.00, change24h: 2.5, volume24h: 1500000000 },
      { symbol: 'USDC', priceUSD: 1.00, change24h: 0.1, volume24h: 5000000000 }
    ];
    setTokenPrices(mockPrices);
  };

  // Get token symbol from address
  const getTokenSymbol = (tokenAddress) => {
    const token = SUPPORTED_TOKENS.find(t =>
      t.address.toLowerCase() === tokenAddress.toLowerCase()
    );
    return token ? token.symbol : 'UNKNOWN';
  };

  // Get token address from symbol
  const getTokenAddress = (symbol) => {
    const token = SUPPORTED_TOKENS.find(t => t.symbol === symbol);
    return token ? token.address : ethers.ZeroAddress;
  };

  // Calculate swap quote locally (no API calls)
  const calculateQuote = async () => {
    if (!fromAmount || !account || parseFloat(fromAmount) <= 0) return;

    setQuoteLoading(true);
    setError(null);

    try {
      // Simple fixed exchange rate simulation (1 ETH = 2500 USDC)
      const ethToUsdcRate = 2500;
      const usdcToEthRate = 1 / ethToUsdcRate;

      let amountOut = 0;
      if (fromToken === 'ETH' && toToken === 'USDC') {
        amountOut = parseFloat(fromAmount) * ethToUsdcRate;
      } else if (fromToken === 'USDC' && toToken === 'ETH') {
        amountOut = parseFloat(fromAmount) * usdcToEthRate;
      } else {
        // Same token swap
        amountOut = parseFloat(fromAmount);
      }

      // Apply fee (0.3%)
      const feePercent = 0.3;
      const feeAmount = amountOut * (feePercent / 100);
      const finalAmountOut = amountOut - feeAmount;

      // Apply slippage
      const slippageMultiplier = 1 - (parseFloat(slippage) / 100);
      const slippageAdjustedAmount = finalAmountOut * slippageMultiplier;

      setToAmount(slippageAdjustedAmount.toFixed(6));

      // Return quote details for display
      return {
        amountOut: slippageAdjustedAmount.toFixed(6),
        feeAmount: feeAmount.toFixed(6),
        feePercent: feePercent.toFixed(1),
        priceImpact: '0.1', // Mock value
        dexRoute: 'Web3FX Contract'
      };
    } catch (err) {
      setError('Failed to calculate quote: ' + err.message);
    } finally {
      setQuoteLoading(false);
    }
  };

  // Execute swap directly on contract
  const executeSwap = async () => {
    if (!fromAmount || !toAmount || !account || !contractRef.current) return;

    setSwapLoading(true);
    setError(null);

    try {
      const tokenIn = getTokenAddress(fromToken);
      const tokenOut = getTokenAddress(toToken);

      if (tokenIn === ethers.ZeroAddress || tokenOut === ethers.ZeroAddress) {
        throw new Error('Invalid token selection');
      }

      // Calculate fee amount (0.3% of output)
      const feePercent = 0.3;
      const feeAmount = parseFloat(toAmount) * (feePercent / 100);

      // Generate unique transaction hash
      const txHash = ethers.keccak256(ethers.toUtf8Bytes(`${account}-${Date.now()}`));

      // Execute swap on contract
      const tx = await contractRef.current.executeSwap(
        tokenIn,
        tokenOut,
        ethers.parseEther(fromAmount),
        ethers.parseEther(toAmount),
        ethers.parseEther(feeAmount.toString()),
        txHash,
        { gasLimit: 350000 }
      );

      // Wait for transaction confirmation
      await tx.wait();

      // Refresh contract data and history
      await fetchContractData(contractRef.current);
      await fetchSwapHistory(contractRef.current, account);

      setError(null);
      alert(`Swap executed successfully! Transaction: ${tx.hash}`);

      // Reset form
      setFromAmount('');
      setToAmount('');

    } catch (err) {
      console.error("Swap execution failed:", err);
      setError('Swap failed: ' + (err.reason || err.message));
    } finally {
      setSwapLoading(false);
    }
  };

  const handleGetQuote = async () => {
    await calculateQuote();
  };

  const handleSwap = async () => {
    await executeSwap();
  };

  const handleFlipTokens = () => {
    setFromToken(toToken);
    setToToken(fromToken);
    setFromAmount(toAmount);
    setToAmount('');
  };

  const refreshData = async () => {
    if (contractRef.current && account) {
      await fetchContractData(contractRef.current);
      await fetchSwapHistory(contractRef.current, account);
      await fetchTokenPrices();
    }
  };

  // ENS Domain Protection - MOVED AFTER ALL HOOKS (correct placement)
  if (!isAuthenticated || !isENSHolder) {
    return (
      <div style={{ textAlign: 'center', color: 'gold', marginTop: '20px' }}>
        <p>Please mint your .xbsys.eth subdomain to access Web3FX.</p>
        <button
          onClick={loginWithSIWE}
          style={{
            background: 'transparent',
            border: '2px solid gold',
            color: 'gold',
            padding: '15px 30px',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
          }}
        >
          Connect Wallet
        </button>
      </div>
    );
  }

  // Styling (preserved from your original code)
  const containerStyle = { background: 'black', padding: '20px', borderRadius: '10px', boxShadow: '0 0 20px gold', transition: 'box-shadow 0.3s ease', maxWidth: '800px', margin: '20px auto' };
  const inputStyle = { width: '100%', padding: '12px', marginBottom: '10px', background: 'black', color: 'gold', border: '1px solid gold', borderRadius: '5px', fontSize: '16px' };
  const buttonStyle = { width: '100%', padding: '12px', background: 'gold', color: 'black', border: 'none', borderRadius: '5px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', marginBottom: '10px' };
  const tabButtonStyle = (active) => ({ padding: '10px 20px', background: active ? 'gold' : 'transparent', color: active ? 'black' : 'gold', border: '1px solid gold', borderRadius: '5px', margin: '0 5px', cursor: 'pointer' });

  return (
    <section className="web3fx-container">
      <Helmet><title>Web3FX.eth - Currency Exchange | BankSystem</title></Helmet>
      <div style={containerStyle} onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 0 40px gold')} onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '0 0 20px gold')}>
        <h1 style={{ textAlign: 'center', color: 'gold', marginBottom: '10px' }}>Web3FX.eth - Currency Exchange</h1>
        <p style={{ textAlign: 'center', color: 'gold', marginBottom: '20px' }}>Secure token swaps with competitive 0.3% fees</p>

        {/* ENS Connection Status Display - Added same component as Web3Markets */}
        <div style={{
          backgroundColor: '#333',
          padding: '1rem',
          borderRadius: '5px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: isAuthenticated ? '#00FF00' : '#FF0000',
            }}></div>
            <span style={{ fontWeight: 'bold', color: isAuthenticated ? '#00FF00' : '#FF0000' }}>
              {isAuthenticated ? 'CONNECTED' : 'DISCONNECTED'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: 'gold', fontSize: '14px' }}>
              {ensName ? `${ensName}` : account ? `${account.substring(0, 6)}...${account.substring(account.length - 4)}` : 'Not connected'}
            </span>
          </div>
        </div>

        {contractRef.current && (
          <div style={{ background: 'rgba(255,215,0,0.1)', border: '1px solid gold', padding: '15px', borderRadius: '5px', marginBottom: '20px', textAlign: 'center' }}>
            <h4 style={{ color: 'gold', margin: '0 0 10px 0' }}>Smart Contract Mode</h4>
            <div style={{ marginTop: '10px', fontSize: '12px', color: 'gold' }}>
              <p>Contract: {WEB3FX_CONTRACT_ADDRESS.slice(0, 10)}...{WEB3FX_CONTRACT_ADDRESS.slice(-8)}</p>
              <p>Total Swaps: {contractData.totalSwaps} | Total Fees: {contractData.totalFees} ETH</p>
              <p>Fee Rate: {contractData.feePercent}% | Min Amount: {contractData.minSwapAmount} ETH</p>
              <p>Revenue Wallet: {contractData.revenueWallet.slice(0, 10)}...{contractData.revenueWallet.slice(-8)}</p>
            </div>
          </div>
        )}

        {error && <div style={{ background: 'rgba(255,0,0,0.1)', border: '1px solid red', color: 'red', padding: '10px', borderRadius: '5px', marginBottom: '20px', textAlign: 'center' }}>{error}</div>}

        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <button style={tabButtonStyle(selectedTab === 'swap')} onClick={() => setSelectedTab('swap')}>Swap</button>
          <button style={tabButtonStyle(selectedTab === 'prices')} onClick={() => setSelectedTab('prices')}>Prices</button>
          <button style={tabButtonStyle(selectedTab === 'history')} onClick={() => setSelectedTab('history')}>History</button>
        </div>

        {selectedTab === 'swap' && (
          <div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: 'gold', display: 'block', marginBottom: '5px' }}>From</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <select value={fromToken} onChange={(e) => setFromToken(e.target.value)} style={{ ...inputStyle, width: '30%' }}>
                  {SUPPORTED_TOKENS.map(token => <option key={token.symbol} value={token.symbol}>{token.symbol}</option>)}
                </select>
                <input type="number" value={fromAmount} onChange={(e) => setFromAmount(e.target.value)} placeholder="0.0" style={{ ...inputStyle, width: '70%' }} />
              </div>
            </div>

            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <button onClick={handleFlipTokens} style={{ background: 'transparent', border: '1px solid gold', color: 'gold', padding: '8px 16px', borderRadius: '50%', cursor: 'pointer' }}>↕</button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: 'gold', display: 'block', marginBottom: '5px' }}>To</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <select value={toToken} onChange={(e) => setToToken(e.target.value)} style={{ ...inputStyle, width: '30%' }}>
                  {SUPPORTED_TOKENS.map(token => <option key={token.symbol} value={token.symbol}>{token.symbol}</option>)}
                </select>
                <input type="number" value={toAmount} placeholder="0.0" readOnly style={{ ...inputStyle, width: '70%', opacity: 0.7 }} />
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: 'gold', display: 'block', marginBottom: '5px' }}>Slippage: {slippage}%</label>
              <input type="range" min="0.1" max="5.0" step="0.1" value={slippage} onChange={(e) => setSlippage(e.target.value)} style={{ width: '100%' }} />
            </div>

            <button onClick={handleGetQuote} disabled={quoteLoading || !fromAmount} style={{ ...buttonStyle, opacity: quoteLoading || !fromAmount ? 0.5 : 1 }}>
              {quoteLoading ? 'Calculating Quote...' : 'Get Quote'}
            </button>

            <button onClick={handleSwap} disabled={swapLoading || !toAmount} style={{ ...buttonStyle, opacity: swapLoading || !toAmount ? 0.5 : 1 }}>
              {swapLoading ? 'Executing Swap...' : 'Execute Swap'}
            </button>
          </div>
        )}

        {selectedTab === 'prices' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: 'gold', margin: 0 }}>Token Prices (Simulated)</h3>
              <button onClick={refreshData} style={{ background: 'transparent', border: '1px solid gold', color: 'gold', padding: '5px 10px', borderRadius: '5px', cursor: 'pointer' }}>Refresh</button>
            </div>
            <div style={{ display: 'grid', gap: '10px' }}>
              {tokenPrices.map(price => (
                <div key={price.symbol} style={{ display: 'flex', justifyContent: 'space-between', padding: '15px', border: '1px solid gold', borderRadius: '5px', background: 'rgba(255,215,0,0.05)' }}>
                  <div>
                    <strong style={{ color: 'gold' }}>{price.symbol}</strong>
                    <div style={{ color: 'gold', fontSize: '12px' }}>Vol: ${(price.volume24h / 1000000).toFixed(1)}M</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: 'gold', fontWeight: 'bold' }}>${price.priceUSD.toFixed(price.priceUSD < 1 ? 6 : 2)}</div>
                    <div style={{ color: price.change24h >= 0 ? '#00ff00' : '#ff0000', fontSize: '12px' }}>
                      {price.change24h >= 0 ? '+' : ''}{price.change24h.toFixed(2)}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedTab === 'history' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: 'gold', margin: 0 }}>Your Swap History</h3>
              <button onClick={refreshData} style={{ background: 'transparent', border: '1px solid gold', color: 'gold', padding: '5px 10px', borderRadius: '5px', cursor: 'pointer' }}>Refresh</button>
            </div>

            {swapHistory.length > 0 ? (
              <div style={{ display: 'grid', gap: '10px' }}>
                {swapHistory.map(swap => (
                  <div key={swap.id} style={{ padding: '15px', border: '1px solid gold', borderRadius: '5px', background: 'rgba(255,215,0,0.05)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                      <span style={{ color: 'gold', fontWeight: 'bold' }}>
                        {swap.amountIn} {swap.fromToken} → {swap.amountOut} {swap.toToken}
                      </span>
                      <span style={{ color: swap.status === 'confirmed' ? '#00ff00' : swap.status === 'failed' ? '#ff0000' : 'gold', fontSize: '12px' }}>
                        {swap.status.toUpperCase()}
                      </span>
                    </div>
                    <div style={{ color: 'gold', fontSize: '12px' }}>
                      Fee: {swap.feeAmount} ETH • {new Date(swap.createdAt).toLocaleDateString()}
                    </div>
                    {swap.txHash && swap.txHash !== ethers.ZeroHash && (
                      <div style={{ color: 'gold', fontSize: '10px', marginTop: '5px' }}>
                        Hash: {swap.txHash.slice(0, 10)}...{swap.txHash.slice(-6)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: 'gold', textAlign: 'center' }}>No swaps yet. Make your first swap!</p>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default Web3FX;
